<script setup>
  import { computed } from "vue";
  import { storeToRefs } from "pinia";
  import ChartPreview from "@/components/Reports/ChartTemplates/ChartPreview.vue";
  import { isNpsTemplate } from "@stores/chartTemplates/chartTemplates";
  import { useChartDataCounts } from "@/composables/useChartDataCounts";
  import { useReportsV2Store } from "@stores/survey/reportsV2/reportsV2";

  const { currentReport } = storeToRefs(useReportsV2Store());

  const props = defineProps({
    element: {
      type: Object,
      required: true,
    },
    bindings: {
      type: Object,
      default: () => ({}),
    },
    chartTemplates: {
      type: Array,
      default: () => [],
    },
    width: {
      type: Number,
      required: true,
    },
    height: {
      type: Number,
      required: true,
    },
  });

  const { getChartSeriesCount, getChartCategoryCount } = useChartDataCounts();

  const binding = computed(() => props.bindings[props.element.name]);

  const chartTemplate = computed(() => {
    if (!binding.value?.chart_template_id) return null;
    return props.chartTemplates.find((t) => t.id === binding.value.chart_template_id) || null;
  });

  const seriesCount = computed(() => {
    if (isNpsTemplate(chartTemplate.value)) return 3;
    return getChartSeriesCount(binding.value, chartTemplate.value?.base_type);
  });

  const categoryCount = computed(() => getChartCategoryCount(binding.value, chartTemplate.value?.base_type));

  const chartPalette = computed(() => {
    const chartStyles = currentReport.value?.report_theme_chart_styles || {};
    // undefined (not []) when the theme has no palette, so ChartPreview falls back to its default colors
    const palette = chartTemplate.value?.report_theme_palette === "nps" ? chartStyles.palette_nps : chartStyles.palette_default;
    return palette?.length ? palette : undefined;
  });
</script>

<template>
  <div
    class="w-full h-full flex items-center justify-center"
    :class="{ 'bg-secondary': !chartTemplate }">
    <ChartPreview
      v-if="chartTemplate"
      :base-type="chartTemplate.base_type"
      :colors="chartPalette"
      :width="width"
      :height="height"
      :series-count="seriesCount"
      :category-count="categoryCount" />
    <span
      v-else
      class="text-foreground-subtle font-medium uppercase tracking-wide text-xs">
      Chart
    </span>
  </div>
</template>
